import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import ProtectedRoute from "@/components/feature/ProtectedRoute";

// Eagerly load auth + home (critical path)
import Home from "@/pages/home/page";
import LoginPage from "@/pages/auth/LoginPage";
import SignupPage from "@/pages/auth/SignupPage";
import CallbackPage from "@/pages/auth/CallbackPage";

// Lazy load everything else
const FeaturesPage = lazy(() => import("@/pages/features/page"));
const GetStartedPage = lazy(() => import("@/pages/get-started/page"));
const PricingPage = lazy(() => import("@/pages/pricing/page"));
const ContactPage = lazy(() => import("@/pages/contact/page"));
const TermsPage = lazy(() => import("@/pages/terms/page"));
const PrivacyPage = lazy(() => import("@/pages/privacy/page"));
const AffiliateToolsPage = lazy(() => import("@/pages/affiliate-tools/page"));
const BlogPage = lazy(() => import("@/pages/blog/page"));
const LandingPagesFeaturePage = lazy(() => import("@/pages/features/landing-pages/page"));
const PostformeCallbackPage = lazy(() => import("@/pages/dashboard/networks/postforme-callback"));

const AIContentPage = lazy(() => import("@/pages/product/ai-content/page"));
const ForumAgentPage = lazy(() => import("@/pages/product/forum-agent/page"));
const MultiNetworkPage = lazy(() => import("@/pages/product/multi-network/page"));
const SchedulingPage = lazy(() => import("@/pages/product/scheduling/page"));
const ProductAnalyticsPage = lazy(() => import("@/pages/product/analytics/page"));

const SkoolGroupsPage = lazy(() => import("@/pages/solutions/skool-groups/page"));
const CourseCreatorsPage = lazy(() => import("@/pages/solutions/course-creators/page"));
const CoachesPage = lazy(() => import("@/pages/solutions/coaches/page"));
const MarketingAgenciesPage = lazy(() => import("@/pages/solutions/marketing-agencies/page"));
const AffiliateMarketersPage = lazy(() => import("@/pages/solutions/affiliate-marketers/page"));

const DashboardLayout = lazy(() => import("@/pages/dashboard/components/DashboardLayout"));
const DashboardOverview = lazy(() => import("@/pages/dashboard/overview/page"));
const NetworksPage = lazy(() => import("@/pages/dashboard/networks/page"));
const AccountsPage = lazy(() => import("@/pages/dashboard/accounts/page"));
const ContentPage = lazy(() => import("@/pages/dashboard/content/page"));
const SchedulePage = lazy(() => import("@/pages/dashboard/schedule/page"));
const AnalyticsPage = lazy(() => import("@/pages/dashboard/analytics/page"));
const SupportPage = lazy(() => import("@/pages/dashboard/support/page"));
const ReferralPage = lazy(() => import("@/pages/dashboard/referral/page"));
const AffiliateDashboardPage = lazy(() => import("@/pages/dashboard/affiliate/page"));
const DashboardSettingsPage = lazy(() => import("@/pages/dashboard/settings/page"));
const SkoolScannerPage = lazy(() => import("@/pages/dashboard/skool-scanner/page"));
const DashboardLandingPagesPage = lazy(() => import("@/pages/dashboard/landing-pages/page"));
// const DashboardForumAgentPage = lazy(() => import("@/pages/dashboard/forum-agent/page"));
const CampaignsPage = lazy(() => import("@/pages/dashboard/campaigns/page"));
const BulkPostsPage = lazy(() => import("@/pages/dashboard/bulk-posts/page"));
const LabelsPage = lazy(() => import("@/pages/dashboard/labels/page"));
const CarPromoterPage = lazy(() => import("@/pages/dashboard/car-promoter/page"));

const AdminLayout = lazy(() => import("@/pages/admin/components/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/overview/page"));
const AdminUsersPage = lazy(() => import("@/pages/admin/users/page"));
const AdminPlansPage = lazy(() => import("@/pages/admin/plans/page"));
const AdminRevenuePage = lazy(() => import("@/pages/admin/revenue/page"));
const AdminNetworksPage = lazy(() => import("@/pages/admin/networks/page"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/settings/page"));
const AdminApiKeysPage = lazy(() => import("@/pages/admin/apikeys/page"));
const AdminLandingPagesPage = lazy(() => import("@/pages/admin/landing-pages/page"));
const AdminSetupPage = lazy(() => import("@/pages/admin-setup/page"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-violet-500 text-3xl"></i>
        </div>
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

const routes: RouteObject[] = [
  { path: "/", element: <Home /> },

  { path: "/features", element: <S><FeaturesPage /></S> },
  { path: "/get-started", element: <S><GetStartedPage /></S> },
  { path: "/pricing", element: <S><PricingPage /></S> },
  { path: "/contact", element: <S><ContactPage /></S> },
  { path: "/terms", element: <S><TermsPage /></S> },
  { path: "/privacy", element: <S><PrivacyPage /></S> },
  { path: "/affiliate-tools", element: <S><AffiliateToolsPage /></S> },
  { path: "/features/landing-pages", element: <S><LandingPagesFeaturePage /></S> },
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  { path: "/auth/callback", element: <CallbackPage /> },
  { path: "/dashboard/networks/postforme-callback", element: <S><PostformeCallbackPage /></S> },
  { path: "/blog", element: <S><BlogPage /></S> },

  { path: "/product/ai-content", element: <S><AIContentPage /></S> },
  { path: "/product/forum-agent", element: <S><ForumAgentPage /></S> },
  { path: "/product/multi-network", element: <S><MultiNetworkPage /></S> },
  { path: "/product/scheduling", element: <S><SchedulingPage /></S> },
  { path: "/product/analytics", element: <S><ProductAnalyticsPage /></S> },

  { path: "/solutions/skool-groups", element: <S><SkoolGroupsPage /></S> },
  { path: "/solutions/course-creators", element: <S><CourseCreatorsPage /></S> },
  { path: "/solutions/coaches", element: <S><CoachesPage /></S> },
  { path: "/solutions/marketing-agencies", element: <S><MarketingAgenciesPage /></S> },
  { path: "/solutions/affiliate-marketers", element: <S><AffiliateMarketersPage /></S> },

  {
    path: "/dashboard",
    element: <ProtectedRoute><S><DashboardLayout /></S></ProtectedRoute>,
    children: [
      { path: "", element: <S><DashboardOverview /></S> },
      { path: "networks", element: <S><NetworksPage /></S> },
      { path: "accounts", element: <S><AccountsPage /></S> },
      { path: "content", element: <S><ContentPage /></S> },
      { path: "schedule", element: <S><SchedulePage /></S> },
      { path: "analytics", element: <S><AnalyticsPage /></S> },
      { path: "support", element: <S><SupportPage /></S> },
      { path: "referral", element: <S><ReferralPage /></S> },
      { path: "affiliate", element: <S><AffiliateDashboardPage /></S> },
      { path: "settings", element: <S><DashboardSettingsPage /></S> },
      { path: "skool-scanner", element: <S><SkoolScannerPage /></S> },
      { path: "landing-pages", element: <S><DashboardLandingPagesPage /></S> },
      // { path: "forum-agent", element: <S><DashboardForumAgentPage /></S> },
      { path: "campaigns", element: <S><CampaignsPage /></S> },
      { path: "bulk-posts", element: <S><BulkPostsPage /></S> },
      { path: "labels", element: <S><LabelsPage /></S> },
      { path: "car-promoter", element: <S><CarPromoterPage /></S> },
    ],
  },

  {
    path: "/admin",
    element: <ProtectedRoute requireAdmin><S><AdminLayout /></S></ProtectedRoute>,
    children: [
      { path: "", element: <S><AdminOverview /></S> },
      { path: "users", element: <S><AdminUsersPage /></S> },
      { path: "plans", element: <S><AdminPlansPage /></S> },
      { path: "revenue", element: <S><AdminRevenuePage /></S> },
      { path: "networks", element: <S><AdminNetworksPage /></S> },
      { path: "landing-pages", element: <S><AdminLandingPagesPage /></S> },
      { path: "settings", element: <S><AdminSettingsPage /></S> },
      { path: "apikeys", element: <S><AdminApiKeysPage /></S> },
      { path: "setup", element: <S><AdminSetupPage /></S> },
    ],
  },

  { path: "*", element: <NotFound /> },
];

export default routes;
